// ============================================================
// Pure decision helpers for the DoseLogModal units editor.
//
// Extracted so the off-protocol semantics are unit-testable:
// these gate WHICH mcg value gets recorded in a dose log, so
// they are safety-critical (see calculations.test.ts culture).
// ============================================================

import { formatUnits } from '../../utils/calculations';

/**
 * True when the user's entered units differ from the draw the app
 * INSTRUCTS (the 0.5-snapped formatUnits value shown on every surface).
 *
 * Compared against the snapped value with a tolerance, NOT the raw
 * protocol units with strict !==. The editor seeds from the snapped
 * display string, so a strict compare against the un-snapped value
 * flagged a no-op open-and-close as off-protocol whenever the true
 * units weren't a clean 0.5 multiple (e.g. 12.3 -> seeded "12.5"),
 * silently switching the recorded mcg. With this rule, typing the
 * instructed value logs identically to never opening the editor.
 *
 * Non-numeric input returns false here; it is blocked from saving by
 * isValidDrawUnits instead.
 */
export function isOffProtocolUnits(customUnits: string, standardUnits: number): boolean {
  if (!customUnits) return false;
  const entered = parseFloat(customUnits);
  if (!Number.isFinite(entered)) return false;
  const snappedStandard = parseFloat(formatUnits(standardUnits));
  return Math.abs(entered - snappedStandard) > 0.05;
}

/**
 * A draw the app is willing to persist: positive and finite. Blocks
 * 0, negative, and NaN entries, which would otherwise log a
 * meaningless 0 mcg dose that still decrements the vial.
 */
export function isValidDrawUnits(units: number): boolean {
  return Number.isFinite(units) && units > 0;
}
